import { Col, createModal, Row, Typography, useModal, useToast } from "@commonlibs";
import { TabsModalProps } from "ui/common/components/feedback/TabsModal/TabsModal.types";
// import { RedeemPointsState } from "./CustomerManagementModal.types";
import useTranslate from "ui/locale/hooks/useTranslate";
import clsx from "clsx";
import Modal from "ui/common/components/feedback/Modal/Modal";
import useVerifyReleaseStrategyMethod from "ui/release-strategy/hooks/useVerifyReleaseStrategyMethod";
import { Operations } from "ui/admins/operations.types";
// import {
//     CancelButton,
//     CheckIconRoot,
//     DeleteFileButton,
//     ExplanationSection,
//     FileUploadError,
//     FileUploadLoading,
//     FileUploadRoot,
//     FileUploadSuccess,
//     ProgressBarRoot,
//     UploadButton,
//     UploadedFiles,
//     UploadSection,
//     WarningIconRoot,
// } from "./CustomerManagementModal.styles";
import FileIcon from "../../../assets/icons/File2.svg";
import { useState } from "react";
import DeleteIcon from "ui/common/icons/DeleteIcon";
import {
    CancelButton,
    CheckIconRoot,
    DeleteFileButton,
    ExplanationSection,
    FileUploadError,
    FileUploadLoading,
    FileUploadRoot,
    FileUploadSuccess,
    ProgressBarRoot,
    UploadButton,
    UploadedFiles,
    UploadSection,
    WarningIconRoot,
} from "ui/transaction/container/CustomerManagementModal/CustomerManagementModal.styles";
import { RedeemPointsState } from "ui/transaction/container/CustomerManagementModal/CustomerManagementModal.types";
import useUpdateTermsPrivacy, { UpdateTermsPrivacyParams } from "features/terms/hooks/useUpdateTermsPrivacy";
import { UpdateTermsAndPrivacyName } from "./UpdateTermsAndPrivacyModal.types";

export interface CustomerManagementModalProps extends Omit<TabsModalProps<RedeemPointsState>, "tabs" | "title"> {
    className?: string;
    style?: React.CSSProperties;
}

enum FileUploadStatus {
    INITIAL,
    LOADING,
    SUCCESS,
    ERROR,
}

export default function createTyCModal(title: string, fileName: UpdateTermsAndPrivacyName) {
    const TyCModal = createModal(({ className, ...rest }: CustomerManagementModalProps) => {
        const [file, setFile] = useState<File | FileList>();
        const [fileUploadedStatus, setFileUploadedStatus] = useState<FileUploadStatus>(FileUploadStatus.INITIAL);
        const [progressValue, setProgressValue] = useState<number>(0);
        const translate = useTranslate();
        const { hideModal } = useModal();
        const translateError = useTranslate("error");
        const { showToast } = useToast();

        const { mutate: updateTermsPrivacy, isLoading: isUpdatingTermsPrivacy } = useUpdateTermsPrivacy({
            onError: () => setFileUploadedStatus(FileUploadStatus.ERROR),
            onSuccess: async () => {
                setProgressValue(100);
                await new Promise((resolve) => setTimeout(resolve, 500));
                setFileUploadedStatus(FileUploadStatus.SUCCESS);
                await new Promise((resolve) => setTimeout(resolve, 800));
                hideModal(TyCModal.id);
            },
        });

        const { verifyReleaseMethod, isValidatingReleaseStrategy } = useVerifyReleaseStrategyMethod({
            onSuccess: async () => {
                setProgressValue(100);
                await new Promise((resolve) => setTimeout(resolve, 500));
                setFileUploadedStatus(FileUploadStatus.SUCCESS);
                await new Promise((resolve) => setTimeout(resolve, 800));
                hideModal(TyCModal.id);
            },
            onError: () => setFileUploadedStatus(FileUploadStatus.ERROR),
            onOtpRequired: () => hideModal(TyCModal.id),
            isLoading: isUpdatingTermsPrivacy
        });

        async function onLoadDocument(file: File): Promise<string> { // skipcq: JS-0116
            const isPdf = file.type === "application/pdf" && file.name.endsWith(".pdf");
            if (!isPdf) {
                showToast(translateError("pdfFileFormatError"), { type: "error" });
                return "";
            }
            setFile(file);
            setFileUploadedStatus(FileUploadStatus.INITIAL);
            return Promise.resolve(file.name);
        }

        async function onSendDocument() {
            setProgressValue(0);
            setFileUploadedStatus(FileUploadStatus.LOADING);
            await new Promise((resolve) => setTimeout(resolve, 100));
            setProgressValue(50);

            const executeAction = (...args: unknown[]) => {
                const [termsData] = args as [UpdateTermsPrivacyParams];
                updateTermsPrivacy(termsData);
            };

            verifyReleaseMethod({
                operationTypeId: Operations.TERMS_AND_CONDITIONS_DOCS_UPDATE,
                originalFn: executeAction,
                originalArgs: [{ [fileName]: file as File } as UpdateTermsPrivacyParams],
            });
        }

        function onDeleteFile() {
            setFile(undefined); // skipcq: JS-W1042
            setFileUploadedStatus(FileUploadStatus.INITIAL);
        }

        function setUploadMessage(): JSX.Element | undefined {
            switch (fileUploadedStatus) {
                case FileUploadStatus.SUCCESS:
                    return (
                        <FileUploadSuccess>
                            <CheckIconRoot />
                            <Typography variant="body3Strong">{translate("uploadCompleted")}</Typography>
                        </FileUploadSuccess>
                    );
                case FileUploadStatus.ERROR:
                    return (
                        <FileUploadError>
                            <WarningIconRoot />
                            <Typography css={{ marginBottom: "5px" }} variant="body3Strong" color="red">
                                {translate("uploadError")}
                            </Typography>
                            <Typography
                                onClick={onSendDocument}
                                css={{ cursor: "pointer", textDecoration: "underline" }}
                                variant="body3Strong"
                                color="red"
                            >
                                {translate("retry")}
                            </Typography>
                        </FileUploadError>
                    );
                default:
                    return (
                        <FileUploadLoading>
                            <Typography css={{ marginBottom: "15px" }} variant="body3Strong">
                                {translate("uploadingElement")}
                            </Typography>
                            <ProgressBarRoot value={progressValue} />
                        </FileUploadLoading>
                    );
            }
        }

        return (
            <Modal className={clsx("add-points-modal", className)} title={title} {...rest}>
                <ExplanationSection>{translate("uploadExplanation4")}</ExplanationSection>
                <UploadSection>
                    {fileUploadedStatus !== FileUploadStatus.INITIAL ? (
                        setUploadMessage()
                    ) : (
                        <FileUploadRoot
                            upload={onLoadDocument}
                            fileTypes={".pdf"}
                            placeholder={
                                <Col alignItems="center" gap={8}>
                                    <img width={35} src={FileIcon} alt="" />
                                    <Typography variant="body3Strong">{`${translate("uploadFileText")} .PDF`}</Typography>
                                </Col>
                            }
                            size="xs"
                            fullWidth
                            keepPlaceholder
                        ></FileUploadRoot>
                    )}
                </UploadSection>
                {file && (
                    <UploadedFiles>
                        <Row justifyContent="space-between" css={{ padding: "5px 0px" }}>
                            <Typography variant="body2Regular">{file instanceof File && file.name}</Typography>
                            <DeleteFileButton data-testid="delete-file-button" onClick={onDeleteFile}>
                                <DeleteIcon />
                            </DeleteFileButton>
                        </Row>
                    </UploadedFiles>
                )}
                <Row justifyContent="center" gap={25}>
                    <CancelButton onClick={() => hideModal(TyCModal.id)}>{translate("cancel")}</CancelButton>
                    <UploadButton
                        onClick={onSendDocument}
                        disabled={!file || fileUploadedStatus === FileUploadStatus.SUCCESS}
                        loading={fileUploadedStatus === FileUploadStatus.LOADING || isValidatingReleaseStrategy}
                    >
                        {translate("uploadFile")}
                    </UploadButton>
                </Row>
            </Modal>
        );
    });
    return TyCModal;
}